import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { CustomisableSection } from '@shared/models/customisable-section';
import { GetSearchResultsResponse } from '@shared/models/get-search-results-response';
import { Inspiration, InspirationFormat, PreLoginInspiration } from '@shared/models/inspiration.interface';
import { PaginatedApiResponse } from '@shared/models/paginated-api-response';
import {
  IPreloginBenefitResponse,
  IPreLoginMemberBenefitResponse,
} from '@shared/models/search/prelogin/prelogin-benefits.model';
import { SearchResult } from '@shared/models/search/search-result';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import { IInspirationFormat } from '@shared/shared/interfaces/article.interfaces';
import { Card, CardStyleEnum } from '@shared/shared/modules/card/models/card';
import { SiteConfigService } from '@shared/shared/services/site-config.service';
import { extractId, replaceTemplateKeys } from '@shared/utils/url.lib';
import { Angulartics2Segment } from 'angulartics2';
import { map, Observable, of, switchMap, take } from 'rxjs';
import { CustomMetaService } from './custom-meta.service';
import { SiteSettingsService } from './site-settings.service';

const DEFAULT_PAGE_SIZE = 12;
const MODERN_PAGE_SIZE = 9;
const IMAGES_LENGTH = 4;
@Injectable({ providedIn: 'root' })
export class PreloginService {
  constructor(
    private http: HttpClient,
    private siteConfService: SiteConfigService,
    private segmentTracking: Angulartics2Segment,
    private siteSettingsService: SiteSettingsService,
    private customMetaService: CustomMetaService
  ) {}

  public getPreloginBenefits(page = 1): Observable<GetSearchResultsResponse> {
    const pageSize =
      this.siteConfService.getComponentData('header')?.default?.layout === 'modern'
        ? MODERN_PAGE_SIZE
        : DEFAULT_PAGE_SIZE;
    const params: Params = {
      page,
      page_size: pageSize,
    };

    return this.http.get<PaginatedApiResponse<any>>(ApiEndpoint.PRELOGIN_BENEFITS_AUTOCOMPLETE, { params }).pipe(
      switchMap((data: PaginatedApiResponse<any>) =>
        of({
          items: data.results.map((res) => this.mapToBenefitsSearchResult(res as IPreloginBenefitResponse)),
          page: params.page,
          pageSize: params.pageSize,
          canLoadMore: !!data.next,
          totalItems: data.count ? data.count : data.results.length,
        })
      )
    );
  }

  private mapToBenefitsSearchResult = (result: IPreloginBenefitResponse): SearchResult => {
    const componentData = this.siteConfService.getComponentData('searchResults');
    const cardConfig: Card = componentData.default;

    let cardStyle = null,
      darkTheme = false;

    if (cardConfig) {
      cardStyle = cardConfig.cardStyle || CardStyleEnum.halfnhalf_alt;
      darkTheme = cardConfig.darkTheme || false;
    }

    return {
      uuid: result?.url,
      id: extractId(result.url),
      url: `/product/benefits/details/${result.id.toString()}`,
      type: 'prelogin-benefits',
      imageUrl: result.images?.[0]?.image || null,
      cardStyle,
      darkTheme,
      body: {
        heading: result.name || result.name_default,
      },
    };
  };

  public getPreloginBenefitDetails(id: number): Observable<IPreloginBenefitResponse> {
    const url = replaceTemplateKeys(ApiEndpoint.PRELOGIN_BENEFIT_DETAILS, { id });

    return this.http.get(url).pipe(
      map((preloginBenefit: IPreloginBenefitResponse) => {
        return { ...preloginBenefit };
      })
    );
  }

  public getPreLoginBenefitsFormat(): Observable<IInspirationFormat[]> {
    return this.http.get<IInspirationFormat[]>(ApiEndpoint.PRELOGIN_BENEFITS_FORMAT);
  }

  public getPreLoginBenefitsByFormats(
    id: number,
    records: number
  ): Observable<PaginatedApiResponse<IPreLoginMemberBenefitResponse>> {
    return this.http.get<PaginatedApiResponse<IPreLoginMemberBenefitResponse>>(
      `${ApiEndpoint.PRELOGIN_BENEFITS_AUTOCOMPLETE}?pre_login_member_benefit_format_id=${id}&page_size=${records}`
    );
  }

  public populateImages(customArray, defaultArray) {
    const targetArray = [];
    for (let i = 0; i < IMAGES_LENGTH; i++) {
      targetArray.push(customArray?.[i]?.file || defaultArray[i]);
    }
    return targetArray;
  }

  public getInspirationContents(): Observable<PaginatedApiResponse<Inspiration>> {
    return this.http.get<PaginatedApiResponse<Inspiration>>(
      ApiEndpoint.PRELOGIN_FEATURED_INSPIRATION + '?featured=true&page=1&page_size=5'
    );
  }

  public getInspirationsByFormat(url: string): Observable<PaginatedApiResponse<PreLoginInspiration>> {
    return this.http.get<PaginatedApiResponse<PreLoginInspiration>>(ApiEndpoint.PRELOGIN_FEATURED_INSPIRATION + url);
  }

  public getPreLoginInspirationFormat(): Observable<InspirationFormat[]> {
    return this.http.get<Array<InspirationFormat>>(ApiEndpoint.PRELOGIN_INSPIRATION_FORMAT + '?ordering=-id');
  }

  public eventTrack(trackName: string, eventData?: object): void {
    let country: string;
    const language = this.siteSettingsService.settings.language;
    const clientName = this.siteSettingsService.settings?.client?.name;
    this.siteConfService.selectedSite
      .pipe(
        take(1),
        map((site) => site.country_code)
      )
      .subscribe((name) => {
        country = name;
      });

    this.segmentTracking.eventTrack(trackName, {
      language,
      clientName,
      country,
      ...eventData,
    });
  }

  public updateMetaInfo(data: CustomisableSection): void {
    this.customMetaService.updateTitle(data?.title);
    this.customMetaService.updateMetaDetails(data?.subtitle);
  }

  public setUrlMetaInfo(): void {
    // sets up alternate and canonical urls in the meta tags
    this.customMetaService.updateCanonicalLink();
  }
}
